import { jsxs, jsx } from 'react/jsx-runtime';
import styled, { css } from 'styled-components';
import { XIcon } from '@minuthq/meatball-ui-icons';
import { ButtonStyleReset } from '../button/button.js';
import { Text } from '../text/text.js';
import { colorScale, colors } from '../../tokens/colors/colors.js';
import '../../tokens/shadows/shadows.js';
import { spacing } from '../../tokens/spacing/spacing.js';
import '../../tokens/breakpoints/breakpoints.js';

function InputContainer({ label, clearAriaLabel, showClearButton, onClear, startAdornment, endAdornment, shrink, error, helperText, children, cursor, disabled, tabIndex, requiredIndicator, size = "medium", rounded = false, }) {
    return (jsxs(Container, { children: [jsxs(Labelled, { "$error": !!error, "$cursor": cursor, "$disabled": disabled, tabIndex: tabIndex, "$rounded": rounded, children: [startAdornment, jsxs(FieldWrapper, { children: [jsx(LabelWrapper, { children: size !== "small" && (jsxs(FloatingLabel, { "$shrink": shrink, children: [label, requiredIndicator && "*"] })) }), jsx(InputWrapper, { "$hasLabel": !!label && size !== "small", "$disabled": disabled, "$size": size, children: children })] }), (!!endAdornment || !!onClear) && (jsxs(EndWrapper, { children: [!!onClear && (jsx(ClearButton, { onClick: onClear, "aria-label": clearAriaLabel, disabled: disabled, "$visibilityHidden": !showClearButton, children: jsx(XIcon, { width: 12 }) })), endAdornment] }))] }), jsxs(BottomWrapper, { children: [!!error && typeof error === "string" && (jsx(Text, { variant: "bodyS", color: "emergency", textAlign: "left", children: error })), helperText && (jsx(Text, { variant: "bodyS", color: "secondary", textAlign: "left", children: helperText }))] })] }));
}
const shrunkLabelCss = css `
  font-size: 0.825rem;
  transform: translateY(-100%);
`;
const LabelWrapper = styled.div `
  position: relative;
  /*
    The minimum height of the container is 60px or 3.75rem,
    this makes sure that when there only is one line of text (regardless of line-height) the label will be centered
  */
  top: 1.875rem;
`;
const FloatingLabel = styled.span `
  position: absolute;
  transform: translateY(-50%);
  font-size: 1rem;
  transition: all 0.2s;
  white-space: nowrap;
  line-height: 1.5rem;

  ${({ $shrink }) => $shrink && shrunkLabelCss}
`;
const FieldWrapper = styled.div `
  flex: 1;
  display: flex;
  align-items: flex-start;
  overflow: hidden;
`;
const InputWrapper = styled.div `
  flex: 1;
  padding-top: ${({ $hasLabel, $size }) => $hasLabel ? `1.75rem` : $size === "small" ? spacing.XS : spacing.M};
  padding-bottom: ${({ $hasLabel, $size }) => $hasLabel ? `${spacing.XS}` : $size === "small" ? spacing.XS : spacing.M};
  color: ${({ $disabled }) => $disabled ? colorScale.blueContrast[400] : colors.textPrimary};
  min-height: 1.5rem;
  overflow: hidden;
`;
const ClearButton = styled.button `
  ${ButtonStyleReset}
  display: flex;
  align-items: center;
  width: 24px;
  height: 24px;
  background-color: transparent;
  color: ${colors.textPrimary};
  visibility: ${({ $visibilityHidden }) => $visibilityHidden ? "hidden" : "visible"};

  &:focus {
    outline-color: ${colorScale.blue[700]};
    outline-offset: 2px;
    outline-width: 1px;
    outline-style: dashed;
  }

  &:disabled {
    color: ${colorScale.blueContrast[400]};
    cursor: auto;
  }
`;
const EndWrapper = styled.div `
  display: flex;
  align-items: center;
  gap: ${spacing.XS};
`;
const Labelled = styled.label `
  position: relative;
  display: flex;
  align-items: center;
  gap: ${spacing.M};
  padding-inline: ${spacing.M};
  background-color: ${colors.neutral};
  border: 1px solid
    ${({ $error }) => ($error ? colors.systemErrorDark : colors.neutral)};
  border-radius: ${({ $rounded }) => ($rounded ? "9999px" : "8px")};
  cursor: ${({ $disabled, $cursor }) => $disabled ? "default" : ($cursor ?? "pointer")};
  outline: none;

  color: ${({ $disabled }) => !$disabled ? colors.textTertiary : colorScale.blueContrast[400]};

  &:focus-within {
    border-color: ${({ $error }) => $error ? colors.systemErrorDark : colors.primary};
  }

  &:has(input:not([value=""])),
  // Looking at the value is not as stable with textarea
  &:has(textarea:not(:empty)),
  &:has(select option:checked:not([value=""])),
  &:has(input:placeholder-shown),
  &:has(textarea:placeholder-shown),
  &:focus-within {
    ${FloatingLabel} {
      ${shrunkLabelCss}
    }
  }
`;
const BottomWrapper = styled.div `
  padding-left: ${spacing.M};
`;
const Container = styled.div `
  width: 100%;
  min-width: 0;
`;

export { InputContainer };
